import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
export const Route = createFileRoute('/_layout/blog/telegram-bot-setup/')({
    component: TelegramBotSetupPost,
    head: () => ({
        meta: [
            { title: 'Set up the Adora Telegram bot in 60 seconds — Adora' },
            {
                name: 'description',
                content: 'Add the Adora bot to your chat, send /chatid, paste it back. Step-by-step setup for routing feedback submissions and reminders to Telegram.',
            },
            { property: 'og:title', content: 'Set up the Adora Telegram bot in 60 seconds — Adora' },
            {
                property: 'og:description',
                content: 'Add the Adora bot to your chat, send /chatid, paste it back. Step-by-step setup for routing feedback submissions and reminders to Telegram.',
            },
        ],
    }),
});
function TelegramBotSetupPost() {
    return (<article className="mx-auto max-w-3xl px-6 md:px-8 py-12">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5"/>
                All posts
            </Link>

            <header className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-wide text-primary">How-to · 2026-04-28</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    Set up the Adora Telegram bot in 60 seconds
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    Three steps. No tokens to paste. No webhook configuration. The bot is already wired —
                    you just need to tell Adora <em>which</em> chat to talk to.
                </p>
            </header>

            <div className="prose prose-neutral dark:prose-invert mt-10 max-w-none">
                <h2>Step 1 — Add the Adora bot to your chat</h2>
                <p>
                    Open Telegram → the chat where you want notifications to land (a group, a channel, or a
                    one-on-one with yourself). Tap the chat title → <strong>Add member</strong> → search for the
                    Adora bot's <code>@username</code>.
                </p>
                <p>
                    Don't know the username? Inside Adora, click the <strong>?</strong> icon next to any
                    "Telegram chat ID" input — it shows the exact handle and a one-click copy.
                </p>

                <h2>Step 2 — Send <code>/chatid</code> in the chat</h2>
                <p>
                    Type one of <code>/chatid</code>, <code>/start</code>, or <code>/whoami</code> and send. The
                    bot will reply with a message like:
                </p>
                <pre><code>{`👋 Adora is connected to this chat.

Chat ID: -1001234567890

Copy this into Adora at /console/internal/settings → Telegram routing
to receive payout / photo reminders here.`}</code></pre>
                <p>
                    Group chat IDs start with <code>-100</code> and are negative. Direct chats are positive
                    integers. Both work.
                </p>

                <h2>Step 3 — Paste the chat ID into Adora</h2>
                <p>Three places, depending on what you're routing:</p>
                <ul>
                    <li>
                        <strong>Workspace default</strong> — <code>/console/workspace</code>. Used by every
                        feedback campaign in this workspace unless overridden.
                    </li>
                    <li>
                        <strong>Per-campaign override</strong> — on a feedback-mode campaign's edit panel. Useful
                        when one specific campaign should fire to a different chat.
                    </li>
                    <li>
                        <strong>Internal driver notifier</strong> (staff-only) —
                        <code>/console/internal/settings</code>. Photo + payout chat IDs for the driver-management
                        module.
                    </li>
                </ul>
                <p>
                    Each input has a <strong>Test</strong> button next to it — click it after pasting, and the
                    bot will send a confirmation message to verify the chat is reachable. If nothing arrives,
                    the bot probably isn't a member of that chat (or the chat ID was copied wrong).
                </p>

                <h2>Common gotchas</h2>
                <ul>
                    <li>
                        <strong>"The bot doesn't reply"</strong> — make sure it's a member of the chat. Bots can
                        only post where they've been added. For groups, you usually also need to make the bot an
                        admin (or at least let it read messages).
                    </li>
                    <li>
                        <strong>"Chat not found"</strong> on the test button — the chat ID is wrong or the bot
                        isn't a member. Re-run <code>/chatid</code> in the right chat to confirm.
                    </li>
                    <li>
                        <strong>Group renamed → still works.</strong> The chat ID is permanent for the lifetime of
                        the chat, even if the title or members change.
                    </li>
                </ul>

                <h2>Next: configure delivery</h2>
                <p>
                    Once the chat ID is set, every feedback submission on that workspace's feedback campaigns
                    will be delivered automatically. Read{' '}
                    <Link to="/blog/telegram-notifications" className="text-primary hover:underline">
                        Real-time Telegram notifications
                    </Link>{' '}
                    for the full map of what Adora sends, where, and why.
                </p>
            </div>

            <footer className="mt-12 border-t pt-6 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5"/>
                    All posts
                </Link>
                <Link to="/sign-up" search={{ redirect: '/console' }} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                    Get started
                    <ArrowRight className="h-4 w-4"/>
                </Link>
            </footer>
        </article>);
}
